/* eslint-disable react/prop-types */
// PropTypes disabled - using TypeScript for type safety
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import InputText, { InputTextType } from 'src/components/atoms/InputText/InputText';
import Row from 'src/components/atoms/Row/Row';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { AutoSuggest } from 'src/components/molecules/AutoSuggest';
import { Dropdown } from 'src/components/molecules/Dropdown';
import { FileInputField } from 'src/components/molecules/FileInputField';
import { STATES } from 'src/constants/states';
import useGooglePlacesAutocomplete from 'src/features/address/hooks/useGooglePlacesAutocomplete';
import { useSalesforceIdField } from 'src/features/company/hooks/useSalesforceIdField';
import { IInformation } from 'src/features/getStartedUI/form/ts/interfaces';
import { isValidSalesforceId, onlyRequestedSymbols, validateName } from 'src/helpers';
import { formatPhone } from 'src/helpers/formatPhone';
import { letterMatchingRegex, websiteRegex } from 'src/helpers/formRegex';
import { IUser, IValueLabel } from 'src/ts/interfaces';
import { ICompany } from 'src/ts/interfaces/company';

// Registration form extends standard types to include password
type RegistrationFormValues = {
  user?: Omit<IUser, 'id' | 'auth_id' | 'company_id'> & { password?: string };
  company: Omit<ICompany, 'id' | 'inserted_at'>;
};

export type RegisterCompanyFormProps = {
  information: Pick<
    IInformation,
    'title' | 'title_form' | 'company_section_title' | 'create_company_user_title'
  >;
  handleOnSubmit: (data: RegistrationFormValues) => void;
  isLoading?: boolean;
};

const RegisterCompanyForm: React.FC<RegisterCompanyFormProps> = (props) => {
  const { information, isLoading, handleOnSubmit } = props;
  const [searchAddress, setSearchAddress] = useState('');
  const {
    placeSuggestions,
    isLoading: isPlacesLoading,
    error: placesError,
    formatPlacesSuggestions,
    setSelectedPlace,
    selectedPlace
  } = useGooglePlacesAutocomplete({
    inputValue: searchAddress
  });

  const methods = useForm<RegistrationFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {
      company: {
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        website: '',
        salesforce_account_id: '',
        logo: undefined
      }
    }
  });
  useEffect(() => {
    if (selectedPlace?.address) {
      methods.setValue('company.address', selectedPlace?.address.street, { shouldDirty: true });
      methods.setValue('company.city', selectedPlace?.address.city, { shouldDirty: true });
      methods.setValue('company.state', selectedPlace?.address.state, { shouldDirty: true });
      methods.setValue('company.zip', selectedPlace?.address.zipCode, { shouldDirty: true });
      methods.trigger('company.address');
      methods.trigger('company.city');
      methods.trigger('company.state');
      methods.trigger('company.zip');
    }
  }, [selectedPlace, methods]);
  const { reset, handleSubmit, control, formState, setError, clearErrors } = methods;

  // Salesforce Account ID validation and availability checking
  const { indicator: salesforceIdIndicator, isLoading: isSalesforceAccountIdCheckLoading } =
    useSalesforceIdField({
      control,
      fieldName: 'company.salesforce_account_id',
      setError,
      clearErrors,
      enabled: true
      // No originalValue for registration form (new company)
    });

  const onSubmit = (data: RegistrationFormValues) => {
    handleOnSubmit({
      ...data,
      company: {
        ...data.company,
        phone: formatPhone(data.company),
        logo: data.company.logo
      }
    });
    reset();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <>
          <Row>
            <Row.Col size={1}>
              <Text variant={TextVariant.h3} color={TextColor.initial}>
                {information.title_form}
              </Text>
            </Row.Col>
          </Row>

          <Gap height={1.5} />
          <Row gap={2}>
            <Row.Col size={1}>
              <Row>
                <Row.Col size={1}>
                  <Text variant={TextVariant.normal} color={TextColor.initial}>
                    {information.company_section_title}
                  </Text>
                </Row.Col>
              </Row>

              <Gap height={0.75} />

              <Row>
                <Row.Col size={1}>
                  <InputText
                    placeholder="Company Name"
                    name="company.name"
                    type={InputTextType.text}
                    config={{
                      required: 'Complete this field',
                      validate: {
                        noSpecialCharacters: (value) => onlyRequestedSymbols(value)
                      },
                      maxLength: { value: 200, message: 'Company name too long' }
                    }}
                  />
                </Row.Col>
              </Row>

              <Row gap={1}>
                <Row.Col size={1}>
                  <Controller
                    name="company.address"
                    control={methods.control}
                    render={({ field: { name } }) => (
                      <AutoSuggest
                        name={name}
                        data={
                          methods.getValues('company.address') && placeSuggestions.length === 0
                            ? [
                                {
                                  data: methods.getValues('company.address'),
                                  value: methods.getValues('company.address'),
                                  label: methods.getValues('company.address')
                                } as IValueLabel
                              ]
                            : formatPlacesSuggestions(placeSuggestions)
                        }
                        placeholder="Street Address"
                        onChange={({ value }) => {
                          setSearchAddress(value.toString());
                        }}
                        onSelect={(item) => {
                          methods.setValue('company.address', item.value);
                          setSelectedPlace({ placeId: item?.metadata?.placeId as string });
                        }}
                        onClear={() => {
                          methods.setValue('company.address', '');
                          methods.setValue('company.city', '');
                          methods.setValue('company.state', '');
                          methods.setValue('company.zip', '');
                          setSearchAddress('');
                        }}
                        isLoading={isPlacesLoading}
                        hasError={placesError !== null}
                        config={{
                          required: 'Complete this field'
                        }}
                        setLabelToValue
                      />
                    )}
                  />
                </Row.Col>
              </Row>

              <Row gap={1}>
                <Row.Col size={1}>
                  <InputText
                    placeholder="City"
                    name="company.city"
                    type={InputTextType.text}
                    config={{
                      required: 'Complete this field',
                      validate: {
                        onlySpecialCharacters: (value) =>
                          letterMatchingRegex.test(value) ||
                          'Should not contain only numbers and symbols',
                        isNameValid: (value) => validateName(value)
                      },
                      maxLength: { value: 50, message: 'City name too long' }
                    }}
                  />
                </Row.Col>
                <Row.Col size={1}>
                  <Controller
                    name="company.state"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Complete this field' }}
                    render={({ field: { name, value, onChange } }) => (
                      <Dropdown
                        valueInitial={value}
                        data={[...STATES]}
                        onChange={({ [name]: selectedOption }) => {
                          onChange(selectedOption.selected.value);
                        }}
                        name="company.state"
                        placeholder="State"
                        size="md"
                      />
                    )}
                  />
                </Row.Col>
              </Row>

              <Row gap={1}>
                <Row.Col size={1}>
                  <InputText
                    placeholder="ZIP Code"
                    name="company.zip"
                    type={InputTextType.text}
                    config={{
                      required: 'Complete this field',
                      validate: {
                        validZip: (zip) =>
                          zip && !/^\d{5}$/.test(zip) ? 'Invalid ZIP code format' : undefined
                      }
                    }}
                  />
                </Row.Col>
                <Row.Col size={1}>
                  <InputText
                    placeholder={'Phone number'}
                    name="company.phone"
                    type={InputTextType.tel}
                  />
                </Row.Col>
              </Row>
              <Row gap={1}>
                <Row.Col size={1}>
                  <InputText
                    placeholder="Website"
                    name="company.website"
                    type={InputTextType.text}
                    config={{
                      validate: {
                        invalidWebsiteString: (v) =>
                          v ? websiteRegex.test(v) || 'Invalid website' : undefined
                      }
                    }}
                  />
                </Row.Col>
                <Row.Col size={1}>
                  <InputText
                    placeholder="Salesforce Account ID"
                    name="company.salesforce_account_id"
                    type={InputTextType.text}
                    config={{
                      required: 'Complete this field',
                      validate: {
                        validSalesforceId: (value) =>
                          isValidSalesforceId(value) ||
                          'Must be a valid 15 or 18 character Salesforce ID'
                      }
                    }}
                    endAndorment={salesforceIdIndicator}
                  />
                </Row.Col>
              </Row>

              <Gap height={0.75} />
              <Button
                name="SubmitInvitationDetails"
                variant={ButtonVariant.contained}
                color={ButtonColor.primary}
                size={ButtonSize.medium}
                isLoading={isLoading}
                isDisabled={
                  isLoading ||
                  Boolean(Object.keys(formState.errors || {}).length) ||
                  !formState.isDirty ||
                  isSalesforceAccountIdCheckLoading
                }
                type={ButtonType.submit}
              >
                {information.create_company_user_title}
              </Button>
            </Row.Col>
            <Row.Col alignContent="center" size={0.5}>
              <FileInputField name="company.logo" title="Company Logo" />
            </Row.Col>
          </Row>
        </>
      </form>
    </FormProvider>
  );
};

export default RegisterCompanyForm;
